import React, { useState, useEffect, useCallback } from 'react';
import { X, Printer, Tag, Check, Wifi, WifiOff, RefreshCw, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const POS_KEY   = 'phonelab_print_config';
const LABEL_KEY = 'phonelab_label_config';

const DEFAULT_POS = {
  printer: '', useQZ: false, width: 80,
  fontSize: 10, margin: 3, lineHeight: 1.4, boldHeader: true,
};

const DEFAULT_LABEL = {
  labelPrinter: 'Clabel-CT221B',
  useLabelPrinter: false,
  labelWidth: 50,
  labelHeight: 30,
  autoAdjust: true,
  fontSizes: { telephone: 28, prix: 24, nomClient: 16, numTicket: 20, panne: 16, date: 12, pinCode: 14 },
  showFields: { telephone: true, prix: true, nomClient: true, numTicket: true, panne: true, date: true, pinCode: true },
};

const DEFAULT_EXAMPLE = {
  telephone: '0612345678',
  nomClient: 'JEAN DUPONT',
  panne: 'Écran cassé + batterie',
  prix: '89',
  numTicket: 'PL260320-001',
  date: '11/04/26',
  pinCode: '1234',
};

// ── Génère l'aperçu étiquette inline ─────────────────────────────────────────
function LabelPreview({ labelCfg, example }) {
  const fs = labelCfg.fontSizes;
  const sh = labelCfg.showFields;
  const w  = Math.min(labelCfg.labelWidth * 3.2, 240);
  const h  = Math.min(labelCfg.labelHeight * 3.2, 140);

  return (
    <div style={{
      width: `${w}px`, height: `${h}px`,
      background: '#fff', border: '1px solid #ccc',
      borderRadius: '3px', padding: '4px 6px',
      fontFamily: '"Courier New", Courier, monospace',
      color: '#000', overflow: 'hidden',
      display: 'flex', flexDirection: 'column', gap: '1px',
      boxShadow: '0 2px 12px rgba(0,0,0,0.5)',
    }}>
      {/* Ligne 1 : numéro ticket + date */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        {sh.numTicket && <span style={{ fontSize: `${fs.numTicket * 0.45}px`, fontWeight: 700 }}>#{example.numTicket}</span>}
        {sh.date      && <span style={{ fontSize: `${fs.date * 0.45}px`, color: '#555' }}>{example.date}</span>}
      </div>
      {/* Nom client */}
      {sh.nomClient && <div style={{ fontSize: `${fs.nomClient * 0.45}px`, fontWeight: 700 }}>{example.nomClient}</div>}
      {/* Téléphone — grand */}
      {sh.telephone && <div style={{ fontSize: `${fs.telephone * 0.45}px`, fontWeight: 700, letterSpacing: '0.5px' }}>{example.telephone}</div>}
      {/* Panne */}
      {sh.panne && <div style={{ fontSize: `${fs.panne * 0.45}px`, color: '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{example.panne}</div>}
      {/* Prix + PIN */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto' }}>
        {sh.prix    && <span style={{ fontSize: `${fs.prix * 0.45}px`, fontWeight: 700 }}>{example.prix}€</span>}
        {sh.pinCode && (
          <span style={{ fontSize: `${fs.pinCode * 0.45}px`, fontWeight: 700, border: '1px solid #000', padding: '0 3px', borderRadius: '2px' }}>
            {example.pinCode}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Section Imprimantes ───────────────────────────────────────────────────────
function SectionImprimantes({ posConfig, setPos, labelConfig, setLabel, printers, qzStatus, onConnectQZ }) {
  return (
    <div className="space-y-4">
      {/* QZ Tray banner */}
      <div className="flex items-center justify-between bg-gray-800 border border-gray-700 rounded-xl px-4 py-3">
        <div className="flex items-center gap-2">
          {qzStatus === 'connected'
            ? <Wifi size={14} className="text-green-400" />
            : <WifiOff size={14} className="text-gray-500" />}
          <span className="text-sm text-white">QZ Tray</span>
          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
            qzStatus === 'connected' ? 'bg-green-500/20 text-green-300' :
            qzStatus === 'connecting' ? 'bg-yellow-500/20 text-yellow-300' :
            'bg-gray-700 text-gray-400'}`}>
            {qzStatus === 'connected' ? 'Connecté' : qzStatus === 'connecting' ? 'Connexion...' : 'Non connecté'}
          </span>
        </div>
        <button onClick={onConnectQZ}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold rounded-xl">
          <RefreshCw size={12} /> {qzStatus === 'connected' ? 'Actualiser' : 'Connecter'}
        </button>
      </div>

      <p className="text-gray-400 text-xs flex items-start gap-1.5">
        <AlertTriangle size={12} className="mt-0.5 text-yellow-500 shrink-0" />
        QZ Tray est requis pour l'impression directe. Téléchargez-le sur <a href="https://qz.io" target="_blank" rel="noreferrer" className="text-blue-400 underline">qz.io</a> et lancez-le avant d'imprimer.
      </p>

      {/* POS Printer */}
      <div className="bg-gray-800 border border-green-700/50 rounded-xl p-4 space-y-3">
        <p className="text-green-400 font-semibold text-sm flex items-center gap-2">
          <Printer size={14} /> Imprimante POS (Tickets &amp; Bons)
        </p>
        <p className="text-gray-400 text-xs">Imprimante thermique pour les tickets de caisse et bons d'entrée</p>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-gray-400 text-[10px] font-bold uppercase tracking-wider block mb-1">Imprimante (liste ou saisie manuelle)</label>
            {printers.length > 0 && (
              <select value={posConfig.printer}
                onChange={e => setPos(p => ({ ...p, printer: e.target.value }))}
                className="w-full bg-gray-700 border border-gray-600 text-white text-sm rounded-xl px-3 py-2 mb-1 focus:outline-none focus:ring-1 focus:ring-green-500">
                <option value="">— Sélectionner dans la liste —</option>
                {printers.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            )}
            <input value={posConfig.printer} onChange={e => setPos(p => ({ ...p, printer: e.target.value }))}
              placeholder="Ou saisir le nom exact (ex: POS-80)"
              className="w-full bg-gray-700 border border-gray-600 text-white text-sm rounded-xl px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500 placeholder-gray-500" />
          </div>
          <div>
            <label className="text-gray-400 text-[10px] font-bold uppercase tracking-wider block mb-1">Largeur du ticket (mm)</label>
            <div className="flex gap-2">
              {[58, 80].map(w => (
                <button key={w} onClick={() => setPos(p => ({ ...p, width: w }))}
                  className={`flex-1 py-2 rounded-xl text-sm font-semibold border transition-all ${posConfig.width === w ? 'bg-green-500 text-white border-green-500' : 'bg-gray-700 text-gray-300 border-gray-600 hover:border-green-500'}`}>
                  {w}mm
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between pt-1">
          <span className="text-gray-400 text-xs">Activer QZ Tray pour ce type</span>
          <div onClick={() => setPos(p => ({ ...p, useQZ: !p.useQZ }))}
            className={`relative w-10 h-5 rounded-full cursor-pointer transition-colors ${posConfig.useQZ ? 'bg-green-500' : 'bg-gray-600'}`}>
            <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${posConfig.useQZ ? 'left-5' : 'left-0.5'}`} />
          </div>
        </div>
      </div>

      {/* Label Printer */}
      <div className="bg-gray-800 border border-orange-700/50 rounded-xl p-4 space-y-3">
        <p className="text-orange-400 font-semibold text-sm flex items-center gap-2">
          <Tag size={14} /> Imprimante Étiquettes (Clabel CT221B ou autre)
        </p>
        <p className="text-gray-400 text-xs">Imprimante d'étiquettes autocollantes pour identifier les appareils</p>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-gray-400 text-[10px] font-bold uppercase tracking-wider block mb-1">Imprimante (liste ou saisie manuelle)</label>
            {printers.length > 0 && (
              <select value={labelConfig.labelPrinter}
                onChange={e => setLabel(c => ({ ...c, labelPrinter: e.target.value }))}
                className="w-full bg-gray-700 border border-gray-600 text-white text-sm rounded-xl px-3 py-2 mb-1 focus:outline-none focus:ring-1 focus:ring-orange-500">
                <option value="">— Sélectionner dans la liste —</option>
                {printers.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            )}
            <input value={labelConfig.labelPrinter} onChange={e => setLabel(c => ({ ...c, labelPrinter: e.target.value }))}
              placeholder="Ou saisir: Clabel-CT221B"
              data-testid="label-printer-input"
              className="w-full bg-gray-700 border border-gray-600 text-white text-sm rounded-xl px-3 py-2 focus:outline-none focus:ring-1 focus:ring-orange-500 placeholder-gray-500" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-gray-400 text-[10px] font-bold uppercase tracking-wider block mb-1">Largeur (mm)</label>
              <input type="number" min="25" max="54" value={labelConfig.labelWidth}
                onChange={e => setLabel(c => ({ ...c, labelWidth: Math.min(54, Math.max(25, +e.target.value || 50)) }))}
                data-testid="label-width-input"
                className="w-full bg-gray-700 border border-gray-600 text-white text-sm rounded-xl px-3 py-2 focus:outline-none focus:ring-1 focus:ring-orange-500 font-mono text-center" />
            </div>
            <div>
              <label className="text-gray-400 text-[10px] font-bold uppercase tracking-wider block mb-1">Hauteur (mm)</label>
              <input type="number" min="15" max="60" value={labelConfig.labelHeight}
                onChange={e => setLabel(c => ({ ...c, labelHeight: Math.min(60, Math.max(15, +e.target.value || 30)) }))}
                data-testid="label-height-input"
                className="w-full bg-gray-700 border border-gray-600 text-white text-sm rounded-xl px-3 py-2 focus:outline-none focus:ring-1 focus:ring-orange-500 font-mono text-center" />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between pt-1">
          <span className="text-gray-400 text-xs">Activer QZ Tray pour les étiquettes</span>
          <div onClick={() => setLabel(c => ({ ...c, useLabelPrinter: !c.useLabelPrinter }))}
            className={`relative w-10 h-5 rounded-full cursor-pointer transition-colors ${labelConfig.useLabelPrinter ? 'bg-orange-500' : 'bg-gray-600'}`}>
            <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${labelConfig.useLabelPrinter ? 'left-5' : 'left-0.5'}`} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Section Étiquettes ────────────────────────────────────────────────────────
function SectionEtiquettes({ labelConfig, setLabel }) {
  const [example, setExample] = useState(() => {
    try { return { ...DEFAULT_EXAMPLE, ...JSON.parse(localStorage.getItem('phonelab_label_example')) }; }
    catch { return DEFAULT_EXAMPLE; }
  });

  useEffect(() => {
    localStorage.setItem('phonelab_label_example', JSON.stringify(example));
  }, [example]);

  const FIELDS = [
    { key: 'telephone', label: 'Téléphone (gras)' },
    { key: 'prix',      label: 'Prix (gras)' },
    { key: 'nomClient', label: 'Nom client (gras)' },
    { key: 'numTicket', label: 'N° Ticket' },
    { key: 'panne',     label: 'Panne' },
    { key: 'date',      label: 'Date' },
    { key: 'pinCode',   label: 'PIN/Code' },
  ];

  return (
    <div className="grid grid-cols-2 gap-4">
      {/* Left: font sizes + visibility */}
      <div className="space-y-3">
        {/* Auto-adjust */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 flex items-center justify-between">
          <div>
            <p className="text-white text-sm font-semibold">Ajustement automatique</p>
            <p className="text-gray-400 text-xs">Ajuste les tailles selon le contenu (plus de texte = plus petit)</p>
          </div>
          <div onClick={() => setLabel(c => ({ ...c, autoAdjust: !c.autoAdjust }))}
            className={`relative w-10 h-5 rounded-full cursor-pointer shrink-0 transition-colors ${labelConfig.autoAdjust ? 'bg-blue-500' : 'bg-gray-600'}`}>
            <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${labelConfig.autoAdjust ? 'left-5' : 'left-0.5'}`} />
          </div>
        </div>

        {/* Font sizes */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
          <p className="text-gray-300 text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-1">
            Tailles de police (px)
            {labelConfig.autoAdjust && <span className="text-blue-400 ml-1">• Ajustées automatiquement</span>}
          </p>
          <div className="grid grid-cols-2 gap-2">
            {FIELDS.map(f => (
              <div key={f.key} className="flex items-center gap-2">
                <input type="checkbox" checked={labelConfig.showFields[f.key]}
                  onChange={e => setLabel(c => ({ ...c, showFields: { ...c.showFields, [f.key]: e.target.checked } }))}
                  className="w-3.5 h-3.5 accent-orange-500 shrink-0" />
                <label className={`text-xs flex-1 truncate ${labelConfig.showFields[f.key] ? 'text-gray-300' : 'text-gray-600'}`}>{f.label}</label>
                <input type="number" min="8" max="60" value={labelConfig.fontSizes[f.key]}
                  disabled={!labelConfig.showFields[f.key]}
                  onChange={e => setLabel(c => ({ ...c, fontSizes: { ...c.fontSizes, [f.key]: Math.min(60, Math.max(8, +e.target.value || 16)) } }))}
                  className="w-12 bg-gray-700 border border-gray-600 text-white text-xs rounded-lg px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-orange-500 font-mono text-center disabled:opacity-30" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right: preview + example data */}
      <div className="space-y-3">
        {/* Live preview */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-gray-300 text-xs font-bold uppercase tracking-wider">Aperçu en temps réel</p>
            <span className="text-gray-500 text-[10px]">Étiquette {labelConfig.labelWidth}mm × {labelConfig.labelHeight}mm</span>
          </div>
          <div className="flex justify-center">
            <LabelPreview labelCfg={labelConfig} example={example} />
          </div>
          <p className="text-gray-600 text-[10px] text-center mt-2">Dimensions réelles : {labelConfig.labelWidth}mm × {labelConfig.labelHeight}mm</p>
        </div>

        {/* Example data */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 space-y-2">
          <p className="text-gray-300 text-xs font-bold uppercase tracking-wider mb-2">Données d'exemple (modifiables)</p>
          {[
            { key: 'telephone', ph: 'Téléphone' },
            { key: 'nomClient', ph: 'Nom client' },
            { key: 'panne',     ph: 'Panne / réparation' },
            { key: 'prix',      ph: 'Prix (€)' },
            { key: 'pinCode',   ph: 'PIN/Code' },
          ].filter(f => labelConfig.showFields[f.key]).map(f => (
            <input key={f.key} value={example[f.key]} onChange={e => setExample(ex => ({ ...ex, [f.key]: e.target.value }))}
              placeholder={f.ph}
              className="w-full bg-gray-700 border border-gray-600 text-white text-xs rounded-xl px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-orange-500 placeholder-gray-500" />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Modal principal ───────────────────────────────────────────────────────────
export const PrintConfigModal = ({ onClose, onConfigSaved }) => {
  const [activeTab, setActiveTab] = useState('imprimantes');
  const [qzStatus,  setQzStatus]  = useState('idle');
  const [printers,  setPrinters]  = useState([]);

  const [posConfig, setPosConfig] = useState(() => {
    try { return { ...DEFAULT_POS, ...JSON.parse(localStorage.getItem(POS_KEY)) }; }
    catch { return DEFAULT_POS; }
  });

  const [labelConfig, setLabelConfig] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(LABEL_KEY));
      return {
        ...DEFAULT_LABEL,
        ...saved,
        fontSizes:  { ...DEFAULT_LABEL.fontSizes,  ...(saved?.fontSizes  || {}) },
        showFields: { ...DEFAULT_LABEL.showFields, ...(saved?.showFields || {}) },
      };
    }
    catch { return DEFAULT_LABEL; }
  });

  const connectQZ = useCallback(async () => {
    if (typeof window.qz === 'undefined') {
      toast.error('QZ Tray non détecté — lancez QZ Tray d\'abord');
      return;
    }
    setQzStatus('connecting');
    try {
      if (!window.qz.websocket.isActive()) await window.qz.websocket.connect({ retries: 3, delay: 1 });
      const list = await window.qz.printers.find();
      setPrinters(Array.isArray(list) ? list : [list]);
      setQzStatus('connected');
      toast.success(`QZ Tray connecté — ${Array.isArray(list) ? list.length : 1} imprimante(s) trouvée(s)`);
    } catch (e) {
      setQzStatus('error');
      toast.error(`QZ Tray: ${e.message}`);
    }
  }, []);

  useEffect(() => {
    if (typeof window.qz !== 'undefined' && window.qz.websocket.isActive()) {
      window.qz.printers.find().then(l => {
        setPrinters(Array.isArray(l) ? l : [l]);
        setQzStatus('connected');
      }).catch(() => {});
    }
  }, []);

  const save = () => {
    localStorage.setItem(POS_KEY,   JSON.stringify(posConfig));
    localStorage.setItem(LABEL_KEY, JSON.stringify(labelConfig));
    onConfigSaved?.(posConfig);
    toast.success('Configuration sauvegardée ✓');
    onClose();
  };

  const reset = () => {
    setPosConfig(DEFAULT_POS);
    setLabelConfig(DEFAULT_LABEL);
  };

  const NAV = [
    { id: 'imprimantes', label: 'Imprimantes', icon: Printer },
    { id: 'etiquettes',  label: 'Étiquettes',  icon: Tag },
  ];

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-4xl flex flex-col max-h-[92vh]"
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700 bg-gray-800 rounded-t-2xl shrink-0">
          <div className="flex items-center gap-2">
            <Printer size={16} className="text-gray-400" />
            <span className="text-white font-bold">Configuration impression</span>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-white rounded-lg hover:bg-gray-700">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-1 min-h-0">
          {/* Sidebar */}
          <div className="w-44 shrink-0 border-r border-gray-700 bg-gray-800/50 rounded-bl-2xl flex flex-col py-3">
            {NAV.map(n => (
              <button key={n.id} onClick={() => setActiveTab(n.id)}
                className={`flex items-center gap-2.5 px-4 py-3 text-sm font-medium transition-all text-left mx-2 rounded-xl mb-1 ${
                  activeTab === n.id
                    ? 'bg-blue-600/20 text-blue-300 border border-blue-600/40'
                    : 'text-gray-400 hover:text-white hover:bg-gray-700/50'}`}>
                <n.icon size={14} />
                {n.label}
                {activeTab === n.id && <Check size={11} className="ml-auto text-blue-400" />}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-5">
            {activeTab === 'imprimantes' && (
              <SectionImprimantes
                posConfig={posConfig} setPos={setPosConfig}
                labelConfig={labelConfig} setLabel={setLabelConfig}
                printers={printers} qzStatus={qzStatus} onConnectQZ={connectQZ}
              />
            )}
            {activeTab === 'etiquettes' && (
              <SectionEtiquettes labelConfig={labelConfig} setLabel={setLabelConfig} />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 px-5 py-4 border-t border-gray-700 bg-gray-800 rounded-b-2xl shrink-0">
          <button onClick={reset} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 border border-gray-600 text-gray-300 text-sm rounded-xl">
            Réinitialiser
          </button>
          <div className="ml-auto flex gap-2">
            <button onClick={onClose} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 border border-gray-600 text-gray-300 text-sm rounded-xl">
              Annuler
            </button>
            <button onClick={save} data-testid="save-print-config-btn"
              className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold rounded-xl">
              <Check size={14} /> Enregistrer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrintConfigModal;
